import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Home, BookOpen, FileCheck, Scale } from 'lucide-react';
import { createPortal } from 'react-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Star, Loader, ArrowLeft } from 'lucide-react';

// Modal Component
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity backdrop-blur-sm" 
        onClick={onClose}
      />
      <div className="relative z-50 bg-white rounded-xl shadow-2xl w-full max-w-lg mx-auto border border-gray-200 animate-slide-up">
        {children}
      </div>
    </div>,
    document.body
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showEligibility, setShowEligibility] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    debtAmount: '',
    termsAccepted: false
  });
  const location = useLocation();

  const debtAmountOptions = [
    { value: '0-50000', label: '0 - 50,000 ₪' },
    { value: '50000-170000', label: '50,000 - 170,000 ₪' },
    { value: '170000+', label: 'מעל 170,000 ₪' }
  ];

  const handleScrollToSection = (sectionId, e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await addDoc(collection(db, 'leads'), {
        ...formData,
        subject: 'בדיקת זכאות להסדרת חובות',
        type: 'eligibility_check',
        createdAt: new Date().toISOString()
      });
      
      setShowEligibility(false);
      setFormData({ fullName: '', phone: '', debtAmount: '', termsAccepted: false });
      window.location.href = '/thank-you';
    } catch (error) {
      console.error('Error submitting eligibility form:', error);
      alert('אירעה שגיאה בשליחת הבדיקה. אנא נסו שוב.');
    } finally {
      setLoading(false);
    }
  };

  const serviceMenuItems = [
    { title: 'חדלות פירעון', icon: <Scale className="w-5 h-5" />, href: '/insolvency-lawyer' },
    { title: 'הוצאה לפועל', icon: <Scale className="w-5 h-5" />, href: '/execution-lawyer' },
    { title: 'הסדר חובות', icon: <Scale className="w-5 h-5" />, href: '/debt-settlement-lawyer' },
    { title: 'פירוק חברות', icon: <Scale className="w-5 h-5" />, href: '/company-liquidation' },
    { title: 'תביעות כספיות', icon: <Scale className="w-5 h-5" />, href: '/monetary-claims-lawyer' },
  ];

  const mainMenuItems = [
    { title: 'דף הבית', icon: <Home className="w-5 h-5" />, href: '/' },
    ...serviceMenuItems,
    {
      title: 'ייפוי כוח מתמשך',
      icon: <FileCheck className="w-5 h-5" />,
      href: '/topic/powerOfAttorney',
    },
  ];

  const contactItem = { 
    title: 'צור קשר', 
    icon: <Phone className="w-5 h-5" />, 
    href: '/contact' 
  };

  const eligibilityItem = { 
    title: 'בדוק זכאות', 
    icon: <FileCheck className="w-5 h-5" />, 
    action: 'eligibility'
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const linkClasses = (isActive) => `
    flex items-center px-3 py-2 rounded-md text-sm font-medium
    transition-all duration-300 group relative
    ${isActive 
      ? `${isScrolled 
          ? 'text-amber-300 bg-gradient-to-r from-amber-600/20 to-yellow-600/10 shadow-lg shadow-amber-500/20'
          : 'text-amber-600 bg-gradient-to-r from-white/90 to-amber-50/90 shadow-lg shadow-black/20 border border-amber-300/30'
        }`
      : `${isScrolled 
          ? 'text-amber-100 hover:text-amber-300 hover:bg-gradient-to-r hover:from-amber-600/20 hover:to-yellow-600/10' 
          : 'text-amber-600  hover:text-amber-200 hover:bg-white/10'
        }`
    }
  `;

  return (
    <>
      <nav 
        className={`
          fixed top-0 left-0 right-0 z-50 font-heebo
          transition-all duration-500
          ${isScrolled 
            ? 'bg-gradient-to-r from-black via-gray-900 to-white backdrop-blur-md shadow-2xl shadow-black/30 border-b border-amber-400/30' 
            : 'bg-gradient-to-r from-black/20 via-gray-900 to-white backdrop-blur-sm'
          }
        `}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-20">
            {/* Logo and Main Menu Items */}
            <div className="flex items-center flex-1">
              <Link to="/" className="flex items-center group">
                <div className="relative">
                  <img 
                    src="/logoChen.jpg" 
                    alt="לוגו מיכלסון" 
                    className="h-12 w-12 rounded-full border-2 border-amber-300/30 group-hover:border-amber-400/60 transition-all duration-300 group-hover:scale-110" 
                  />
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </Link>
              
              {/* Desktop Main Menu */}
              <div className="hidden md:flex items-center mr-8 space-x-2 space-x-reverse flex-wrap">
                {mainMenuItems.map((item) => (
                    <Link
                      key={item.title}
                      to={item.href}
                      onClick={(e) => {
                        if (item.href.includes('#')) {
                          handleScrollToSection(item.href.substring(item.href.indexOf('#') + 1), e);
                        }
                      }}
                      className={linkClasses(location.pathname === item.href)}
                    >
                      {item.icon && (
                        <span className="ml-1 group-hover:text-amber-500 transition-colors duration-300">
                          {item.icon}
                        </span>
                      )}
                      <span className="text-xs lg:text-sm whitespace-nowrap">{item.title}</span>
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-400 to-yellow-500 group-hover:w-full transition-all duration-300"></div>
                    </Link>
                ))}
              </div>
            </div>

            {/* Contact and Eligibility Links - Always on the left */}
            <div className="hidden md:flex items-center space-x-3 space-x-reverse">
              {/* Eligibility Check Button */}
              <button
                onClick={() => setShowEligibility(true)}
                className={`
                  flex items-center px-4 py-2 rounded-lg text-sm font-medium
                  transition-all duration-300 group relative transform hover:scale-105
                  ${isScrolled
                    ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800'
                    : 'bg-gradient-to-r from-green-500/80 to-green-600/80 text-white hover:from-green-600 hover:to-green-700'
                  }
                  shadow-lg hover:shadow-xl hover:shadow-green-500/30
                  border border-green-400/30 hover:border-green-300/60
                `}
              >
                <FileCheck className="w-4 h-4 ml-2" />
            בדיקת זכאות
              </button>
              
              {/* Contact Button */}
              <Link
                to={contactItem.href}
                className={`
                  flex items-center px-4 py-2 rounded-lg text-sm font-medium
                  transition-all duration-300 group relative transform hover:scale-105
                  ${isScrolled
                    ? 'bg-gradient-to-r from-amber-600 to-yellow-600 text-white hover:from-amber-700 hover:to-yellow-700'
                    : 'bg-gradient-to-r from-amber-500/80 to-yellow-500/80 text-white hover:from-amber-600 hover:to-yellow-600'
                  }
                  shadow-lg hover:shadow-xl hover:shadow-amber-500/30
                  border border-amber-400/30 hover:border-amber-300/60
                `}
              >
                {contactItem.icon && <span className="ml-2">{contactItem.icon}</span>}
                {contactItem.title}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
                  transition-all duration-300 p-2 rounded-lg
                  ${isScrolled 
                    ? 'text-gray-700 hover:text-amber-600 hover:bg-amber-50' 
                    : 'text-white hover:text-amber-300 hover:bg-white/10'
                  }
                `}
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-gradient-to-br from-black/95 via-gray-900/95 to-amber-900/95 backdrop-blur-md shadow-2xl border-b border-amber-300/30">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {/* Main menu items */}
                {mainMenuItems.map((item) => (
                  <Link
                    key={item.title}
                    to={item.href}
                    className={`
                      flex items-center px-3 py-3 rounded-lg text-base font-medium
                      transition-all duration-300 group
                      ${location.pathname === item.href 
                        ? 'text-amber-300 bg-gradient-to-r from-amber-600/30 to-yellow-600/20 border-l-4 border-amber-400' 
                        : 'text-amber-100 hover:text-amber-200 hover:bg-gradient-to-r hover:from-amber-600/20 hover:to-yellow-600/10'
                      }
                    `}
                    onClick={(e) => {
                      if (item.href.includes('#')) {
                        handleScrollToSection(item.href.substring(item.href.indexOf('#') + 1), e);
                      }
                      setIsOpen(false);
                    }}
                  >
                    {item.icon && (
                      <span className="ml-2 group-hover:text-amber-500 transition-colors duration-300">
                        {item.icon}
                      </span>
                    )}
                    {item.title}
                  </Link>
                ))}
                
                {/* Eligibility button */}
                <button
                  onClick={() => {
                    setShowEligibility(true);
                    setIsOpen(false);
                  }}
                  className="
                    flex items-center px-3 py-3 rounded-lg text-base font-medium
                    transition-all duration-300 group w-full
                    text-green-100 hover:text-green-200 hover:bg-gradient-to-r hover:from-green-600/20 hover:to-green-600/10
                  "
                >
                  <span className="ml-2 group-hover:text-green-500 transition-colors duration-300">
                    <FileCheck className="w-5 h-5" />
                  </span>
                  {eligibilityItem.title}
                </button>
                
                {/* Contact link */}
                <Link
                  to={contactItem.href}
                  className="
                    flex items-center px-3 py-3 rounded-lg text-base font-medium
                    transition-all duration-300 group
                    text-amber-100 hover:text-amber-200 hover:bg-gradient-to-r hover:from-amber-600/20 hover:to-yellow-600/10
                  "
                  onClick={() => setIsOpen(false)}
                >
                  {contactItem.icon && (
                    <span className="ml-2 group-hover:text-amber-500 transition-colors duration-300">
                      {contactItem.icon}
                    </span>
                  )}
                  {contactItem.title}
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Eligibility Modal */}
      <Modal isOpen={showEligibility} onClose={() => setShowEligibility(false)}>
        <div className="p-6" dir="rtl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-gray-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">בדיקת זכאות</h3>
            </div>
            <button
              onClick={() => setShowEligibility(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-700 text-sm leading-relaxed">
              נתקלתם בקשיים כלכליים, עיקולים או הגבלות? בדקו בקלות האם אתם זכאים להליך הסדרת חובות.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">שם מלא</label>
              <input
                type="text"
                name="fullName"
                required
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-400 focus:border-gray-400 outline-none transition-all"
                placeholder="הכנס שם מלא"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">טלפון</label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-400 focus:border-gray-400 outline-none transition-all"
                placeholder="הכנס מספר טלפון"
                dir="rtl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">מה גובה החוב שלכם?</label>
              <div className="space-y-2">
                {debtAmountOptions.map((option) => (
                  <label key={option.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="debtAmount"
                      value={option.value}
                      checked={formData.debtAmount === option.value}
                      onChange={handleInputChange}
                      required
                      className="sr-only"
                    />
                    <div className={`
                      w-full p-3 rounded-lg border transition-all duration-300 font-medium
                      ${formData.debtAmount === option.value 
                        ? 'bg-gray-100 border-gray-400 text-gray-800' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }
                    `}>
                      <span className="flex-1 text-center">{option.label}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

        <div className="flex items-start gap-3 pt-2">
  <input
    type="checkbox"
    name="termsAccepted"
    id="terms"
    required
    checked={formData.termsAccepted}
    onChange={handleInputChange}
    className="mt-1 w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
  />
  <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
    אני מאשר/ת את{' '}
    <a 
      href="/terms" 
      target="_blank"
      className="text-gray-800 underline hover:text-gray-600 transition-colors font-medium"
    >
      תנאי השימוש
    </a>
    {' '}ו
    <a 
      href="/privacy" 
      target="_blank"
      className="text-gray-800 underline hover:text-gray-600 transition-colors font-medium"
    >
      מדיניות הפרטיות
    </a>
    {' '}של האתר וקבלת עדכונים ופניות מהמשרד בנושא בדיקת הזכאות והסדרת חובות
  </label>
</div>

           <button
  type="submit"
  disabled={loading || !formData.termsAccepted}
  className={`
    w-full p-3 rounded-lg text-lg font-bold flex items-center justify-center gap-3
    ${loading || !formData.termsAccepted
      ? 'bg-gray-300 cursor-not-allowed opacity-50' 
      : 'bg-gray-800 hover:bg-gray-900'
    }
    text-white transition-all duration-300
  `}
>
  {loading ? (
    <>
      <Loader className="w-5 h-5 animate-spin" />
      <span>בודק זכאות...</span>
    </>
  ) : (
    <>
      <FileCheck className="w-5 h-5" />
      <span>בדק זכאות עכשיו</span>
    </>
  )}
</button>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default Navbar;